/**
 * B6 · A node on the levels path.
 *
 * ## Three rules this component may not break
 *
 * 1. **State is NEVER communicated by color alone.** Every node carries an
 *    icon and text besides the chromatic emphasis: a lock, a play, "12 of
 *    24", a check, or a diploma. Whoever can't tell colors apart still has
 *    to be able to play.
 * 2. **Not a single JavaScript animation.** The available node's pulse is a
 *    `@keyframes` from `styles/global.css`, because the global
 *    `prefers-reduced-motion` guard neutralizes CSS durations and **would
 *    not reach** a `requestAnimationFrame`. That's why this file doesn't
 *    mention the media query: doing so would mean the animation slipped
 *    past the guard (there's an invariant test that checks it).
 * 3. **A locked node explains why.** `blockedBy` is the key of the
 *    criterion that failed; it's translated into an actionable sentence. A
 *    mute lock generates one support ticket per student.
 */
import { type Component, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Award, Check, Flag, Lock, Play } from "@fleet/icons";
import { cn, type StageStatus } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";

/** Actionable sentence from the key of the criterion that blocked it. */
function blockedReason(
  t: ReturnType<typeof useI18n>["t"],
  blockedBy: string | null | undefined,
): string {
  switch (blockedBy) {
    case "stage_completed": {
      return t(msg`Finish the previous level first.`);
    }
    case "placement_test": {
      return t(msg`Pass the placement test to jump straight to this level.`);
    }
    case "entitlement": {
      return t(msg`Complete the payment for this level to unlock it.`);
    }
    case "cohort_active": {
      return t(msg`You need an active enrolment in a running group.`);
    }
    default: {
      return t(msg`This level isn't open for you yet.`);
    }
  }
}

/** Textual status label (the NON-chromatic signal, always visible). */
function statusLabel(
  t: ReturnType<typeof useI18n>["t"],
  status: StageStatus,
  progressPct: number,
): string {
  switch (status) {
    case "locked": {
      return t(msg`Locked`);
    }
    case "available": {
      return t(msg`Start`);
    }
    case "in_progress": {
      const pct = Math.round(progressPct);
      return t(msg`${pct}% done`);
    }
    case "completed": {
      return t(msg`Completed`);
    }
    default: {
      return t(msg`Certified`);
    }
  }
}

const StatusIcon: Component<{ status: StageStatus; milestone?: boolean }> = (props) => (
  <Show when={!props.milestone} fallback={<Flag class="size-6" aria-hidden="true" />}>
    <Show when={props.status !== "locked"} fallback={<Lock class="size-6" aria-hidden="true" />}>
      <Show
        when={props.status !== "certified"}
        fallback={<Award class="size-6" aria-hidden="true" />}
      >
        <Show
          when={props.status !== "completed"}
          fallback={<Check class="size-6" aria-hidden="true" />}
        >
          <Play class="size-6" aria-hidden="true" />
        </Show>
      </Show>
    </Show>
  </Show>
);

/** Visual emphasis per status. MEANING lives in the icon and the text. */
function toneFor(status: StageStatus): string {
  switch (status) {
    case "locked": {
      return "border-dashed border-border bg-muted text-muted-foreground opacity-70";
    }
    case "available": {
      return "border-accent bg-accent/10 text-accent-foreground";
    }
    case "in_progress": {
      return "border-accent bg-background text-foreground";
    }
    case "completed": {
      return "border-accent bg-accent text-accent-foreground";
    }
    default: {
      return "border-accent bg-accent text-accent-foreground";
    }
  }
}

export interface StageNodeProps {
  title: string;
  status: StageStatus;
  progressPct: number;
  blockedBy?: string | null;
  milestone?: boolean;
  /** Configurable level label ("Level 2", "Module 2"…). */
  stageLabel: string;
  testid?: string;
  onActivate?: () => void;
}

export const StageNode: Component<StageNodeProps> = (props) => {
  const { t } = useI18n();
  const locked = (): boolean => props.status === "locked";

  return (
    <button
      type="button"
      data-testid={props.testid}
      data-status={props.status}
      // `aria-disabled` instead of `disabled`: the locked node STAYS
      // clickable on purpose, because clicking it is when it explains
      // what's missing. Disabling it would take it out of focus and leave
      // the student without an answer.
      aria-disabled={locked()}
      onClick={() => props.onActivate?.()}
      class={cn(
        "path-node flex w-full max-w-xs items-center gap-3 rounded-2xl border-2 p-4 text-left",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        toneFor(props.status),
        props.milestone && "path-node--milestone",
        `path-node--${props.status}`,
      )}
    >
      <span
        class="flex size-11 shrink-0 items-center justify-center rounded-full border border-current/30"
        aria-hidden="true"
      >
        <StatusIcon status={props.status} milestone={props.milestone} />
      </span>
      <span class="min-w-0 flex-1">
        <span class="block truncate text-xs font-medium uppercase tracking-wide opacity-80">
          {props.stageLabel}
        </span>
        <span class="block truncate font-semibold">{props.title}</span>
        <span class="block truncate text-sm opacity-90">
          {statusLabel(t, props.status, props.progressPct)}
        </span>
        <Show when={locked()}>
          <span class="mt-1 block text-xs opacity-90">{blockedReason(t, props.blockedBy)}</span>
        </Show>
      </span>
    </button>
  );
};
